import React, { useState, useEffect } from "react";
import { Box, Dialog, DialogContent, IconButton, InputAdornment } from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import DateInput from "app/common/components/input/Date";
import { ColumnName, commonInputStyles, SavingSpinnerForColumnEditors, ActionButtonsForColumnEditors, handleClose, ErrorMessageViewer } from "./ColumnEditorFactory";
import COLORS from "scss/colors.module.scss";
import moment from "moment";
import { validateDateInputsBeforeSaving } from "../RowId/Validations";

const DateTimeReusableEditor = ({
  initialValue = "",
  open,
  data,
  column,
  columnName,
  onSave,
  onCancel,
}) => {
    const [value, setValue] = useState(initialValue);
    const [isSaving, setIsSaving] = useState(false);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [validationError, setValidationError] = useState({ isInvalid: false, message: "" });

    const columnTypeId = String(column?.column_type_id ?? "4");  // Assuming 4 = date, else datetime
    const isDateTime = columnTypeId === "16"; // follows DateColumnEditor logic

    useEffect(() => {
        if (open) {
            setValue(initialValue ?? "");
            setIsPickerOpen(false);
        }
        return () => {
            setValidationError({ isInvalid: false, message: "" });
        }
    }, [initialValue, open]);

    const handleChange = (newDate) => {
        setValue(newDate);
        if (validationError.isInvalid) {
            setValidationError({ isInvalid: false, message: "" });
        }
    };

    const formatDateBeforeSaving = (value, columnTypeId) => {
        if (!value) return null;
        if (columnTypeId == "4") { 
            if (!moment(value, ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss"], true).isValid()) {
                setValidationError({ isInvalid: true, message: "Invalid date format." });
                return false;
            }
            return moment(value).format("YYYY-MM-DDT00:00:00");
        } 
        else if (columnTypeId == "16") { 
            if (moment(value, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss"], true).isValid()) {
                return moment(value).format("YYYY-MM-DDTHH:mm:ss");
            } 
            else if (moment(value, ["YYYY-MM-DD"], true).isValid()) {
                return moment(value).format("YYYY-MM-DDT00:00:00");
            } 
            else {
                setValidationError({ isInvalid: true, message: "Invalid date time format." });
                return false;
            }
        }
        return null;
    }

    const handleSave = async () => {
        setIsSaving(true);
        try {
            let formattedValue = value === "" ? value : formatDateBeforeSaving(value, columnTypeId);

            if (formattedValue === null) {
                onCancel();
                return;
            }

            if (!formattedValue && value !== "") {
                setIsSaving(false);
                return;
            }

            const normalizeForCompare = (str) => str?.replace(" ", "T");
            if (normalizeForCompare(initialValue) === normalizeForCompare(formattedValue)) {
                onCancel();
                return;
            }

            const isUpdateAllowed = validateDateInputsBeforeSaving(value, column, data);
            if (isUpdateAllowed.isInvalid) {
                setValidationError(isUpdateAllowed);
                return;
            }

            await onSave(formattedValue, initialValue, column);
        } catch (err) {
            console.error("Error saving value:", err);
            setValidationError({ isInvalid: true, message: "Invalid date value" });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(value, initialValue, onCancel, validationError, setValidationError)}
            fullWidth
            PaperProps={{
                style: {
                width: "420px",
                height: validationError.isInvalid ? "190px" : "160px",
                borderRadius: "8px",
                },
            }}
        >
            <DialogContent>
                <Box display="flex" flexDirection="column" style={{ width: "100%", gap: 5 }}>
                    {columnName && <ColumnName columnName={columnName} />}

                    <DateInput
                        value={value}
                        isDateTime={isDateTime}
                        format={column?.normalizedColumnOptions?.format}
                        isPickerOpenByDefault={false}
                        underLine={false}
                        isFormattedDate={true}
                        saveOnEnter={true}
                        autoFocus={false}
                        onChange={handleChange}
                        openOnIconClick={isPickerOpen}
                        InputProps={{
                            style: {
                                ...commonInputStyles,
                                width: 370,
                                height: 38,
                                padding: 10
                            },
                            endAdornment: (
                                <InputAdornment position="end" style={{ marginRight: -8 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setIsPickerOpen(true)}
                                        style={{ padding: 4 }}
                                    >
                                        <CalendarTodayIcon style={{ fontSize: 18, color: COLORS.TEXT_COLOR }} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {validationError.isInvalid && (<ErrorMessageViewer errMsg={validationError.message} />)}

                    <ActionButtonsForColumnEditors
                        handleSave={handleSave}
                        handleCancel={() => handleClose(value, initialValue, onCancel, validationError, setValidationError)}
                        isSaveDisabled={isSaving}
                        isCloseDisabled={isSaving}
                    />

                    {isSaving && <SavingSpinnerForColumnEditors />}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default DateTimeReusableEditor;
